import { useState } from 'react';
import { Navigate, useNavigate, useSearch } from '@tanstack/react-router';
import { useToast } from '@/primer/components';
import { PageLoadingState } from '@/components/layout';
import { InviteErrorScreen, InviteErrorVariant } from '@/components/ui';
import { useInitiateOtp, useInvite, usePreAuthenticateInvite } from '@/api';
import { inviteEmailSchema } from '@/schemas';
import {
  classifyPreAuthenticateError,
  clearOtpChallenge,
  DEFAULT_API_ERROR_MESSAGE,
  getApiErrorMessage,
  getApiErrorMessages,
  InviteAuthFailure,
  OTP_TOAST,
  storeOtpChallenge,
} from '@/utils';
import type { ApiErrorMappingOptions } from '@/utils';
import { useInviteAuth } from '@/contexts/InviteAuthContext';
import { AuthEmailForm } from '@/components/ui/AuthEmailForm';

const EMAIL_SIGN_IN_UNAVAILABLE =
  "Email sign-in couldn't load. Try refreshing the page, or continue with a social account";
const OTP_SEND_FAILED = "We couldn't send a verification code. Please try again.";

const INVITE_ERROR_OPTIONS: ApiErrorMappingOptions = {
  messageMap: {
    ERROR_INITIATING_OTP: OTP_SEND_FAILED,
    'Unable to send code': OTP_SEND_FAILED,
  },
  customHandler: (error) =>
    getApiErrorMessages(error).some((message) => message.toLowerCase().includes('email'))
      ? 'Enter a valid email address.'
      : undefined,
};

export function InviteEmail() {
  const { token } = useSearch({ from: '/_public/invite' });
  const { data: invite, isLoading, error } = useInvite(token || '');
  const preAuthenticateInvite = usePreAuthenticateInvite();
  const initiateOtp = useInitiateOtp();
  const navigate = useNavigate();
  const inviteAuth = useInviteAuth();
  const { showToast } = useToast();

  const [typedEmail, setTypedEmail] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState('');
  const [blockedBy, setBlockedBy] = useState<InviteAuthFailure | null>(null);
  const [isEmailSignInUnavailable, setIsEmailSignInUnavailable] = useState(false);

  if (inviteAuth?.blockedUntil !== undefined) {
    return <Navigate to="/invite/auth" search={{ token }} replace />;
  }

  if (isLoading) {
    return <PageLoadingState size="large" />;
  }

  if (error || !invite || blockedBy === InviteAuthFailure.INVALID_INVITE) {
    return <InviteErrorScreen variant={InviteErrorVariant.GENERIC} />;
  }

  if (blockedBy === InviteAuthFailure.CONFLICT) {
    return <InviteErrorScreen variant={InviteErrorVariant.BAD_REQUEST} />;
  }

  const knownEmail = invite.email ?? '';
  const isReturningCreator = Boolean(invite.hasSignedInBefore) && knownEmail !== '';
  const email = typedEmail ?? knownEmail;
  const canContinue =
    (isReturningCreator || inviteEmailSchema.safeParse({ email }).success) &&
    !isEmailSignInUnavailable;
  const isSubmitting = preAuthenticateInvite.isPending || initiateOtp.isPending;

  const handleStepError = (err: unknown, isPreAuthenticate: boolean) => {
    if (inviteAuth?.handleBlockedError(err)) return;
    const failure = classifyPreAuthenticateError(err);

    if (failure === InviteAuthFailure.INVALID_INVITE || failure === InviteAuthFailure.CONFLICT) {
      showToast(OTP_TOAST.REQUEST_FAILED);
      setBlockedBy(failure);
    } else if (failure === InviteAuthFailure.SERVER_ERROR && isPreAuthenticate) {
      setIsEmailSignInUnavailable(true);
      setSubmitError(EMAIL_SIGN_IN_UNAVAILABLE);
    } else {
      setSubmitError(getApiErrorMessage(err, INVITE_ERROR_OPTIONS));
    }
  };

  const handleNext = async () => {
    if (!canContinue || isSubmitting) return;
    setSubmitError('');
    clearOtpChallenge();
    const emailToSubmit = isReturningCreator ? undefined : email;

    try {
      await preAuthenticateInvite.mutateAsync({
        magicToken: token,
        preAuthenticateInviteRequest: { email: emailToSubmit },
      });
    } catch (err) {
      handleStepError(err, true);
      return;
    }

    let otpChallenge;
    try {
      otpChallenge = await initiateOtp.mutateAsync({ magicToken: token, email: emailToSubmit });
    } catch (err) {
      handleStepError(err, false);
      return;
    }

    if (!storeOtpChallenge(token, otpChallenge, { email: emailToSubmit })) {
      setSubmitError(DEFAULT_API_ERROR_MESSAGE);
      return;
    }
    showToast(OTP_TOAST.CODE_SENT);
    navigate({ to: '/invite/otp', search: { token } });
  };

  return (
    <AuthEmailForm
      email={email}
      onEmailChange={setTypedEmail}
      onSubmit={() => void handleNext()}
      isReadOnly={isReturningCreator}
      canContinue={canContinue}
      isSubmitting={isSubmitting}
      submitError={submitError}
      testIdPrefix="invite-email"
    />
  );
}

export default InviteEmail;
